import { Controller } from 'stimulus'

export default class extends Controller {
  connect() {
    this.setupDragAndDrop()
  }

  get draggableRows() {
    return Array.from(this.element.querySelectorAll('tr[data-form-field-id]'))
  }

  setupDragAndDrop() {
    this.draggableRows.forEach((row) => {
      this.setupRowDragAndDrop(row)
    })
  }

  setupRowDragAndDrop(row) {
    // Make row draggable
    row.setAttribute('draggable', 'true')
    row.style.cursor = 'move'

    // Remove old listeners to prevent duplicates
    row.removeEventListener('dragstart', this.boundHandleDragStart)
    row.removeEventListener('dragover', this.boundHandleDragOver)
    row.removeEventListener('drop', this.boundHandleDrop)
    row.removeEventListener('dragend', this.boundHandleDragEnd)
    row.removeEventListener('touchstart', this.boundHandleTouchStart)
    row.removeEventListener('touchmove', this.boundHandleTouchMove)
    row.removeEventListener('touchend', this.boundHandleTouchEnd)

    // Create bound functions if they don't exist
    if (!this.boundHandleDragStart) {
      this.boundHandleDragStart = this.handleDragStart.bind(this)
      this.boundHandleDragOver = this.handleDragOver.bind(this)
      this.boundHandleDrop = this.handleDrop.bind(this)
      this.boundHandleDragEnd = this.handleDragEnd.bind(this)
      this.boundHandleTouchStart = this.handleTouchStart.bind(this)
      this.boundHandleTouchMove = this.handleTouchMove.bind(this)
      this.boundHandleTouchEnd = this.handleTouchEnd.bind(this)
    }

    // Desktop drag events
    row.addEventListener('dragstart', this.boundHandleDragStart)
    row.addEventListener('dragover', this.boundHandleDragOver)
    row.addEventListener('drop', this.boundHandleDrop)
    row.addEventListener('dragend', this.boundHandleDragEnd)

    // Touch events for mobile
    row.addEventListener('touchstart', this.boundHandleTouchStart, { passive: false })
    row.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false })
    row.addEventListener('touchend', this.boundHandleTouchEnd)
  }

  handleDragStart(e) {
    this.draggedElement = e.target.closest('tr')
    this.draggedElement.style.opacity = '0.5'
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', this.draggedElement.innerHTML)
  }

  handleDragOver(e) {
    if (e.preventDefault) {
      e.preventDefault()
    }
    e.dataTransfer.dropEffect = 'move'

    const targetRow = e.target.closest('tr')
    if (targetRow && targetRow !== this.draggedElement && targetRow.hasAttribute('draggable')) {
      const rect = targetRow.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2

      if (e.clientY < midpoint) {
        targetRow.parentNode.insertBefore(this.draggedElement, targetRow)
      } else {
        targetRow.parentNode.insertBefore(this.draggedElement, targetRow.nextSibling)
      }
    }

    return false
  }

  handleDrop(e) {
    if (e.stopPropagation) {
      e.stopPropagation()
    }
    return false
  }

  handleDragEnd(e) {
    this.draggedElement.style.opacity = '1'
    this.updatePositions()
    this.draggedElement = null
  }

  // Touch events for mobile support
  handleTouchStart(e) {
    this.touchedElement = e.target.closest('tr')
    this.touchedElement.classList.add('dragging')

    const touch = e.touches[0]
    this.touchStartY = touch.clientY

    // Create a ghost element for visual feedback
    this.ghostElement = this.touchedElement.cloneNode(true)
    this.ghostElement.style.position = 'fixed'
    this.ghostElement.style.left = '0'
    this.ghostElement.style.right = '0'
    this.ghostElement.style.opacity = '0.8'
    this.ghostElement.style.zIndex = '1000'
    this.ghostElement.style.pointerEvents = 'none'
    this.ghostElement.style.backgroundColor = '#f0f0f0'
    document.body.appendChild(this.ghostElement)

    this.updateGhostPosition(touch.clientY)
  }

  handleTouchMove(e) {
    if (!this.touchedElement) return

    e.preventDefault()
    const touch = e.touches[0]

    this.updateGhostPosition(touch.clientY)

    // Find the row we're hovering over
    const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY)
    const targetRow = elementAtPoint && elementAtPoint.closest('tr[draggable="true"]')

    if (targetRow && targetRow !== this.touchedElement) {
      const rect = targetRow.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2

      if (touch.clientY < midpoint) {
        targetRow.parentNode.insertBefore(this.touchedElement, targetRow)
      } else {
        targetRow.parentNode.insertBefore(this.touchedElement, targetRow.nextSibling)
      }
    }
  }

  handleTouchEnd(e) {
    if (!this.touchedElement) return

    this.touchedElement.classList.remove('dragging')

    if (this.ghostElement) {
      this.ghostElement.remove()
      this.ghostElement = null
    }

    this.updatePositions()
    this.touchedElement = null
  }

  updateGhostPosition(y) {
    if (this.ghostElement) {
      this.ghostElement.style.top = `${y - 30}px`
    }
  }

  updatePositions() {
    const formFieldIds = this.draggableRows.map(row => row.dataset.formFieldId)
    const url = this.element.dataset.formFieldSortableUrlValue
    const csrfToken = document.querySelector('[name="csrf-token"]').content

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ form_field_ids: formFieldIds })
    })
    .then(response => {
      if (!response.ok) {
        console.error('Failed to update positions')
        location.reload()
      }
    })
    .catch(error => {
      console.error('Error updating positions:', error)
      location.reload()
    })
  }
}
